import { Trans, useLingui } from '@lingui/react/macro';
import { type ReactNode, useMemo } from 'react';
import { useIdentification } from '@/account/kyc/useIdentification';
import { useMerchant } from '@/account/merchant-profile/api';
import { MerchantStatusBadge } from '@/account/merchant-profile/MerchantStatusBadge';
import type { WorkspaceType } from '@/base/constants';
import { FeeTierPopover } from '@/fee/FeeTierPopover';
import { Gate, useGate } from '@/gate';
import { useSettings } from '@/settings/api';
import { useCoins } from '@/shared/api';
import { Badge } from '@/shared/Badge';
import { Coin } from '@/shared/Coin';
import { Digits } from '@/shared/Digits';
import { useExternalLinks } from '../../useExternalLinks';

export type HeaderLink = {
  key: string;
  label?: ReactNode;
  level?: 1 | 2;
  link?: string | [string, WorkspaceType];
  onClick?: () => void;
  badge?: ReactNode;
  hidden?: boolean;
  loading?: boolean;
  children?: HeaderLink[];
  more?: ReactNode;
};

export const useHeaderLinks = (): HeaderLink[] => {
  const { t } = useLingui();
  const [{ language }] = useSettings();
  const gate = useGate();
  const links = useExternalLinks();
  const identification = useIdentification();
  const merchant = useMerchant('me');
  const [{ displayQuote }] = useSettings();
  const coins = useCoins();
  const topCoins = useMemo(
    () =>
      coins.data
        ?.filter((x) => x.on_market)
        .sort(
          (a, b) =>
            parseFloat(b.volume_usdt || '0') - parseFloat(a.volume_usdt || '0'),
        ),
    [coins.data],
  );

  return [
    {
      key: 'dashboard',
      link: ['/dashboard', 'app'],
      label: t`Dashboard`,
      level: 1,
    },
    {
      key: 'markets',
      label: t`Markets`,
      level: 1,
      loading: coins.isLoading,
      link: [`/${language}/buy-and-sell`, 'public'],
      children:
        topCoins?.map((coin) => ({
          key: coin.slug,
          link: [`/${language}/buy-and-sell/${coin.slug}`, 'public'],
          label: (
            <div className="flex justify-between gap-3 items-center w-full">
              <div className="flex items-center gap-1 grow">
                <Coin coin={coin} size={6} variant="icon" />
                <div className="text-xs">
                  <Trans>Buy {coin.name}</Trans>
                </div>
              </div>
              <div className="flex flex-col leading-tight items-end">
                <Digits
                  value={coin[`price_${displayQuote}`]}
                  coin={displayQuote}
                  className="text-xs font-medium"
                  abbreviate
                />
                <Digits
                  value={coin.price_change_percent}
                  unit="%"
                  sign="always"
                  color="auto"
                  className="text-2xs"
                  abbreviate
                />
              </div>
            </div>
          ),
        })) ?? [],
      more: t`See Markets`,
    },
    {
      key: 'prices',
      label: t`Prices`,
      level: 1,
      loading: coins.isLoading,
      link: [`/${language}/price`, 'public'],
      children:
        topCoins?.map((coin) => ({
          key: coin.slug,
          link: [`/${language}/price/${coin.slug}`, 'public'],
          label: (
            <div className="flex justify-between gap-3 items-center w-full">
              <div className="flex items-center gap-1 grow">
                <Coin coin={coin} size={6} variant="icon" />
                <div className="text-xs">
                  <Trans>{coin.name} Price</Trans>
                </div>
              </div>
              <div className="flex flex-col leading-tight items-end">
                <Digits
                  value={coin[`price_${displayQuote}`]}
                  coin={displayQuote}
                  className="text-xs font-medium"
                  abbreviate
                />
                <Digits
                  value={coin.price_change_percent}
                  unit="%"
                  sign="always"
                  color="auto"
                  className="text-2xs"
                  abbreviate
                />
              </div>
            </div>
          ),
        })) ?? [],
      more: t`See Prices`,
    },
    {
      key: 'p2p-trade',
      label: t`P2P Trading`,
      level: 1,
      link: ['/trade/p2p', 'app'],
      hidden: !gate([
        Gate.hooks.hasFeature('p2p'),
        Gate.hooks.isStandalone(false),
      ]),
    },
    {
      key: 'trades',
      label: t`Trade`,
      level: 1,
      hidden:
        !gate([Gate.hooks.isStandalone(false)]) ||
        !gate([Gate.hooks.hasFeature('trade')]),
      children: [
        {
          key: 'instant-trade',
          label: t`Instant Trading`,
          link: ['/trade/instant', 'app'],
        },
        {
          key: 'pro-trade',
          label: t`Pro Trading`,
          link: ['/trade/spot', 'app'],
        },
        {
          key: 'grid-bot',
          label: t`Grid Bot`,
          badge: <Badge size={4} variant="notice">{t`Beta`}</Badge>,
          link: ['/trade/grid-bot', 'app'],
          hidden: !gate([Gate.hooks.hasFeature('grid-bot')]),
        },
      ],
    },
    { key: 'blog', label: t`Blog`, link: links.blog, level: 1 },
    {
      key: 'guide',
      label: t`Guide`,
      link: [`/${language}/guide`, 'public'],
      level: 1,
    },
    {
      key: 'profile',
      link: ['/account', 'app'],
      label: t`Profile`,
      level: 2,
      badge: (
        <div className="flex items-center gap-1">
          <FeeTierPopover />
          <Badge size={4} variant={identification.variant} icon>
            {identification.label}
          </Badge>
        </div>
      ),
      hidden: !gate([Gate.hooks.hasAccessToken]),
    },
    {
      key: 'merchant',
      label: t`Merchant`,
      link: ['/merchant/profile/me', 'app'],
      level: 2,
      badge: merchant.data && (
        <MerchantStatusBadge status={merchant.data?.status} size={4} />
      ),
      hidden:
        !(
          gate([Gate.hooks.hasFeature('withdraw-cash')]) ||
          gate([Gate.hooks.hasFeature('deposit-cash')]) ||
          gate([Gate.hooks.hasFeature('p2p')])
        ) || !gate([Gate.hooks.isMerchant(false)]),
    },
    {
      key: 'become-a-merchant',
      label: t`Become a Merchant`,
      link: ['/merchant/apply', 'app'],
      level: 2,
      hidden:
        !(
          gate([Gate.hooks.hasFeature('withdraw-cash')]) ||
          gate([Gate.hooks.hasFeature('deposit-cash')]) ||
          gate([Gate.hooks.hasFeature('p2p')])
        ) || gate([Gate.hooks.isMerchant(false)]),
    },
    {
      key: 'admin',
      label: t`Admin Panel`,
      link: ['/users', 'admin'],
      level: 2,
      hidden: !gate([Gate.hooks.isAdmin]),
    },
    {
      key: 'referral',
      label: t`Invite Your Friends`,
      link: ['/account/referral', 'app'],
      level: 2,
      hidden: !gate([Gate.hooks.hasAccessToken]),
    },
    {
      key: 'settings',
      label: t`Settings`,
      link: ['/settings', 'app'],
      level: 2,
    },
  ];
};
